import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "../AppLayout";
import {
  getMcpScopesCatalog,
  submitMcpOAuthConsent,
} from "../api/McpConnectionApi";
import { get, getCurrentAuthState } from "../api/api";

type WorkspaceRow = { id: string; name: string };

/**
 * OAuth consent landing for MCP hosts (story 3.25).
 * Opened via /authorize → redirect here with PKCE query params.
 */
export const McpOAuthConsentPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = useQuery();

  const clientId = query.get("client_id") || "";
  const redirectUri = query.get("redirect_uri") || "";
  const codeChallenge = query.get("code_challenge") || "";
  const codeChallengeMethod = query.get("code_challenge_method") || "S256";
  const state = query.get("state");
  const requestedScope = query.get("scope") || "";

  const [workspaces, setWorkspaces] = useState<WorkspaceRow[]>([]);
  const [workspaceId, setWorkspaceId] = useState("");
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [selectedScopes, setSelectedScopes] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /** Always show the full catalog; client-requested scopes are only the default selection. */
  const scopeList = useMemo(() => {
    const fromCatalog = Object.keys(labels);
    if (fromCatalog.length) return fromCatalog;
    if (requestedScope.trim()) {
      return requestedScope.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
    }
    return [];
  }, [requestedScope, labels]);

  useEffect(() => {
    (async () => {
      const auth = await getCurrentAuthState();
      if (!auth?.jwt) {
        const returnTo = encodeURIComponent(
          `${window.location.pathname}${window.location.search}`
        );
        window.location.href = `/app/login?redirect_url=${returnTo}`;
        return;
      }
      try {
        const [{ data: ws }, catalog] = await Promise.all([
          get({ path: `/workspaces` }),
          getMcpScopesCatalog(),
        ]);
        const rows: WorkspaceRow[] = (ws || []).map((w: any) => ({
          id: w.id || w.workspaceId,
          name: w.name || w.id,
        }));
        setWorkspaces(rows);
        if (rows.length === 1) setWorkspaceId(rows[0].id);
        const catalogLabels = catalog.labels || {};
        setLabels(catalogLabels);
        const requested = requestedScope.trim()
          ? requestedScope.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean)
          : [];
        // Prefer full catalog selection when the client only asked for a narrow subset
        // (e.g. Cursor copying a minimal WWW-Authenticate scope=). User can uncheck.
        const catalogScopes = catalog.scopes?.length
          ? catalog.scopes
          : Object.keys(catalogLabels);
        const initial =
          requested.length > 0 && requested.length >= catalogScopes.length
            ? requested
            : catalogScopes.length
              ? catalogScopes
              : requested;
        setSelectedScopes(new Set(initial));
      } catch (e: any) {
        setError(e?.message || t("Failed to load consent data"));
      }
    })();
  }, [requestedScope, t]);

  const toggleScope = (scope: string) => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  };

  const onApprove = async () => {
    setError(null);
    if (!clientId || !redirectUri || !codeChallenge || !workspaceId) {
      setError(t("Missing OAuth parameters or workspace"));
      return;
    }
    setSubmitting(true);
    try {
      const { redirectUrl } = await submitMcpOAuthConsent({
        clientId,
        redirectUri,
        codeChallenge,
        codeChallengeMethod,
        workspaceId,
        scope: Array.from(selectedScopes).join(" "),
        state,
      });
      window.location.href = redirectUrl;
    } catch (e: any) {
      setError(e?.message || t("Consent failed"));
      setSubmitting(false);
    }
  };

  const onDeny = () => {
    if (redirectUri) {
      const url = new URL(redirectUri);
      url.searchParams.set("error", "access_denied");
      if (state) url.searchParams.set("state", state);
      window.location.href = url.toString();
      return;
    }
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-lg bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <h1 className="text-xl font-semibold text-gray-900">{t("Connect TypeCRM MCP")}</h1>
        <p className="mt-2 text-sm text-gray-600">
          {t("Approve access for")} <span className="font-medium">{clientId || "MCP client"}</span>
        </p>

        <label className="block mt-6 text-sm font-medium text-gray-700">{t("Workspace")}</label>
        <select
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          value={workspaceId}
          onChange={(e) => setWorkspaceId(e.target.value)}
        >
          <option value="">{t("Select a workspace")}</option>
          {workspaces.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>

        <div className="mt-6">
          <div className="text-sm font-medium text-gray-700">{t("Scopes")}</div>
          <p className="mt-1 text-xs text-gray-500">
            {t("Includes Content Studio and Social posting when enabled below.")}
          </p>
          <ul className="mt-2 space-y-2 max-h-64 overflow-auto">
            {(scopeList.length ? scopeList : Object.keys(labels)).map((scope) => (
              <li key={scope} className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={selectedScopes.has(scope)}
                  onChange={() => toggleScope(scope)}
                />
                <span>
                  <span className="font-medium">{labels[scope] || scope}</span>
                  <span className="block text-xs text-gray-400">{scope}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

        <div className="mt-8 flex gap-3 justify-end">
          <button
            type="button"
            onClick={onDeny}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t("Deny")}
          </button>
          <button
            type="button"
            disabled={submitting || !workspaceId}
            onClick={onApprove}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {submitting ? t("Approving…") : t("Approve")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default McpOAuthConsentPage;
